import React, { useState } from 'react';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import MaskInput from 'react-native-mask-input';

import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { USER_ROLE } from '@/types/user-role';
import { brazilPhoneMask, formatBrazilPhone, getBrazilPhoneError } from '@/utils/brazil-phone';
import { getApiErrorMessage } from '@/utils/api-error';
import { createThemedStyles } from '@/hooks/use-theme-colors';

type Step = 'phone' | 'code';

interface PhoneAuthPanelProps {
  /** Papel da conta criada quando o telefone ainda não tem uma. */
  role?: USER_ROLE;
  /**
   * `auth` (padrão): entra/cadastra pelo número.
   * `link`: gruda o número na conta já logada — o login por email/Google
   * continua valendo, e o WhatsApp passa a ser mais uma porta.
   */
  mode?: 'auth' | 'link';
  /** Chamado no sucesso do modo `link`. */
  onLinked?: () => void;
  /**
   * Guarda rodada ao tocar em "Enviar código". Retornar `false` aborta o envio
   * — o campo segue livre, mas o toque não passa. O caller mostra o toast.
   */
  onBeforeSend?: () => boolean;
}

const useMaskInputStyle = createThemedStyles((th) => ({
  height: 48,
  width: '100%' as const,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: th('#E5E5E5'),
  backgroundColor: th('#FFFFFF'),
  paddingHorizontal: 16,
  paddingVertical: 8,
  fontSize: 16,
  color: th('#0A0A0A'),
  borderCurve: 'continuous' as const,
}));

/**
 * Entrada por WhatsApp: telefone, código, pronto.
 *
 * Um componente só para login e cadastro porque o backend não distingue os
 * dois — o mesmo número entra na conta que existe ou abre uma nova.
 */
export function PhoneAuthPanel({
  role = USER_ROLE.PROVIDER,
  mode = 'auth',
  onLinked,
  onBeforeSend,
}: PhoneAuthPanelProps) {
  const maskInputStyle = useMaskInputStyle();
  const { requestPhoneCode, loginWithPhone, linkPhone } = useAuthStore();
  const isLink = mode === 'link';

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const phoneError = getBrazilPhoneError(phone);

  const sendCode = async () => {
    if (onBeforeSend && !onBeforeSend()) return false;
    if (phoneError) {
      setError(phoneError);
      return false;
    }
    try {
      setBusy(true);
      setError(null);
      const { devCode } = await requestPhoneCode(phone);
      // Em dev, a API devolve o código quando o WhatsApp não está de pé.
      if (devCode) {
        setCode(devCode);
        showToast(`Código (dev): ${devCode}`, 'info');
      }
      setStep('code');
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar o código.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (code.replace(/\D/g, '').length !== 6) {
      setError('O código tem 6 dígitos.');
      return;
    }
    try {
      setBusy(true);
      setError(null);
      if (isLink) {
        await linkPhone({ phone, code });
        onLinked?.();
      } else {
        await loginWithPhone({ phone, code, role });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Código inválido.'));
    } finally {
      setBusy(false);
    }
  };

  /** Cadastro do time comercial: a conta abre sem esperar o código chegar. */
  const skipCode = async () => {
    if (onBeforeSend && !onBeforeSend()) return;
    try {
      setBusy(true);
      setError(null);
      if (isLink) {
        await linkPhone({ phone });
        onLinked?.();
      } else {
        await loginWithPhone({ phone, code: '', role, skipCode: true });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Entrada sem código indisponível neste ambiente.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View className="gap-3">
      {!!error && (
        <View className="flex-row items-start gap-2 rounded-2xl bg-red-50 p-3">
          <Ionicons className="text-error-600" name="alert-circle" size={17} />
          <ThemedText className="flex-1 text-sm leading-5 text-red-700">{error}</ThemedText>
        </View>
      )}

      {step === 'phone' ? (
        <>
          <ThemedText className="text-sm text-neutral-500">
            Enviamos um código por WhatsApp.
          </ThemedText>

          <MaskInput
            placeholder="(11) 99999-9999"
            placeholderTextColor="#A3A3A3"
            value={phone}
            onChangeText={(_masked, unmasked) => setPhone(unmasked)}
            mask={brazilPhoneMask}
            keyboardType="phone-pad"
            autoComplete="tel"
            editable={!busy}
            style={maskInputStyle}
          />

          {/* O aviso só aparece depois que o número tem tamanho de telefone —
              cobrar DDD inexistente a cada dígito digitado seria ruído. */}
          {!!phoneError && phone.replace(/\D/g, '').length >= 10 && (
            <ThemedText className="text-xs text-red-600">{phoneError}</ThemedText>
          )}

          <PressableScale
            haptic
            disabled={busy || !!phoneError}
            onPress={sendCode}
            className="mt-1 h-12 w-full flex-row items-center justify-center gap-2 rounded-full bg-primary"
            style={{ borderCurve: 'continuous' }}
          >
            {busy ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <ThemedText className="font-bold text-sm text-white">Enviar código</ThemedText>
            )}
          </PressableScale>

          {__DEV__ && (
            <Pressable
              disabled={busy}
              onPress={skipCode}
              className="items-center rounded-xl border border-amber-300 bg-amber-50/80 py-3"
              style={Platform.OS === 'web' ? { cursor: 'pointer' } : undefined}
            >
              <ThemedText className="font-semibold text-xs text-amber-800">
                Entrar sem código (dev)
              </ThemedText>
            </Pressable>
          )}
        </>
      ) : (
        <>
          <ThemedText className="text-sm text-neutral-500">
            Código enviado para{' '}
            <ThemedText className="font-semibold text-sm text-neutral-700">
              {formatBrazilPhone(phone)}
            </ThemedText>
            .
          </ThemedText>

          <Input
            placeholder="000000"
            value={code}
            onChangeText={setCode}
            keyboardType="number-pad"
            maxLength={6}
            className="text-center text-2xl tracking-[8px]"
            editable={!busy}
          />

          <PressableScale
            haptic
            disabled={busy}
            onPress={submitCode}
            className="mt-1 h-12 w-full flex-row items-center justify-center gap-2 rounded-full bg-primary"
            style={{ borderCurve: 'continuous' }}
          >
            {busy ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <ThemedText className="font-bold text-sm text-white">Confirmar</ThemedText>
            )}
          </PressableScale>

          <View className="flex-row items-center justify-center gap-6">
            <Pressable
              disabled={busy}
              onPress={async () => {
                setCode('');
                const sent = await sendCode();
                if (sent) showToast('Código reenviado!', 'success');
              }}
              className="py-2"
            >
              <ThemedText className="font-semibold text-sm text-neutral-500">Reenviar</ThemedText>
            </Pressable>
            <Pressable
              disabled={busy}
              onPress={() => {
                setCode('');
                setError(null);
                setStep('phone');
              }}
              className="py-2"
            >
              <ThemedText className="font-semibold text-sm text-neutral-500">
                Trocar número
              </ThemedText>
            </Pressable>
          </View>
        </>
      )}
    </View>
  );
}
